/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable consistent-return */
import MuiButton from '@brands/Components/Button/Button';
import PatientIntakeNavigation from '@brands/Components/PatientIntakeNavigation/PatientIntakeNavigation';
import { useOrganizationFlags, useWindowSize } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { notifySelectedCase } from '@brands/services/cases/selectedCase';
import { CaseType } from '@brands/services/cases/types/ICase';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectCurrentPatientState } from '@brands/store/selectors/currentPatientState';
import { selectOrganization } from '@brands/store/selectors/organization';
import { selectPatientForm } from '@brands/store/selectors/patientForm';
import { selectPatient } from '@brands/store/selectors/selectedPatient';
import { setDependentId, setIsFromSelectPatient } from '@brands/store/slices/pageStateSlice';
import { setExternalForms, setForm, setSelectedPatient } from '@brands/store/slices/patientFormSlice';
import { setSelectedPatientByAdmin } from '@brands/store/slices/selectedPatientSlice';
import { checkExternalFormAnswers } from '@brands/Utils/checkExternalForms';
import { DEFAULT_PATIENT_LOCATION_STATE } from '@brands/Utils/localStorage';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Button } from '@mui/material';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import Container from '../../../../Components/Container/Container';
import { UserObject } from '../SelectPatient/utils/types';
import explainResultsImage from './assets/images/explain-results.png';
import videoCallImage from './assets/images/video-call.png';
import styles from './styles.module.scss';

const SelectCaseType = (): JSX.Element => {
  const screenSize = useWindowSize();
  const { values, selectedPatient: formSelectedPatient } = useAppSelector(selectPatientForm);
  const { selectedPatient: storedSelectedPatient } = useAppSelector(selectPatient);
  const dispatch = useDispatch();
  const roleNavigate = useRoleNavigate();
  const { currentOrganization } = useAppSelector(selectOrganization);
  const currentState = useAppSelector(selectCurrentPatientState);
  const { state } = useLocation();
  const { userInfo } = useAppSelector(selectAuth);

  const [selectedCaseType, setSelectedCaseType] = React.useState<string>('');

  const { videoCaseEnabled, asyncCaseEnabled } = useOrganizationFlags(
    currentState[userInfo.id] || DEFAULT_PATIENT_LOCATION_STATE,
  );

  const getSelectedPatient = (): UserObject | undefined => {
    const selectedPatientState = state?.selectedPatient as
      | ({ selected_patient?: UserObject } & Partial<UserObject>)
      | undefined;

    const routeSelectedPatient =
      selectedPatientState?.selected_patient ?? (selectedPatientState as UserObject | undefined);
    if (routeSelectedPatient?.id || routeSelectedPatient?.patient) return routeSelectedPatient;

    const storedPatientId = storedSelectedPatient?.id ?? formSelectedPatient?.value;
    if (storedPatientId === undefined || storedPatientId === null || storedPatientId === '') return undefined;

    const selectedPatientId = Number(storedPatientId);
    if (!Number.isFinite(selectedPatientId)) return undefined;

    return {
      id: selectedPatientId.toString(),
      patient: (storedSelectedPatient ?? (selectedPatientId === Number(userInfo.id) ? userInfo : null)) as UserObject[
        'patient'
      ],
      patientFullName: formSelectedPatient?.label || storedSelectedPatient?.full_name || userInfo.full_name,
    };
  };

  const checkExternalForms = async (): Promise<string[]> => {
    const selectedPatient = getSelectedPatient();
    const [checkProfile, checkMedicalHistory, checkMedicalConditionsFormAnswer, checkDentalHistoryFormAnswer] =
      await checkExternalFormAnswers(userInfo, selectedPatient?.patient || null);

    const formsToSet: string[] = [];

    if (!checkProfile) {
      formsToSet.push('patient_profile');
    }
    if (!checkMedicalHistory) {
      formsToSet.push('patient_medical_history');
    }
    if (!checkMedicalConditionsFormAnswer) {
      formsToSet.push('patient_medical_conditions');
    }
    if (!checkDentalHistoryFormAnswer) {
      formsToSet.push('patient_dental_history');
    }

    dispatch(setExternalForms(formsToSet));
    return formsToSet;
  };

  const fetchData = (): Promise<string[]> => {
    return checkExternalForms();
  };

  const onSubmit = async (): Promise<void> => {
    await notifySelectedCase(selectedCaseType);

    const selectedPatient = getSelectedPatient();
    if (!selectedPatient) {
      return;
    }
    const selectedPatientProfile = selectedPatient.patient;
    const selectedPatientId = Number(selectedPatientProfile?.id ?? selectedPatient.id ?? userInfo.id);
    if (!Number.isFinite(selectedPatientId) || selectedPatientId <= 0) return;
    const selectedPatientState = selectedPatientProfile?.address?.state ?? userInfo.address?.state;

    const payload = [...values!];
    const filteredPayload = payload.filter((item) => item.form_question_id !== 20);
    filteredPayload.push({
      form_question_id: 20,
      value: {
        caseType: selectedCaseType,
      },
    });

    dispatch(setForm(filteredPayload));

    if (selectedPatient.patientFullName === 'Myself') {
      dispatch(setIsFromSelectPatient(true));
      dispatch(setDependentId(null));
      roleNavigate('/create-your-profile', {
        state: { caseType: selectedCaseType },
      });
    } else if (selectedPatient.patientFullName === 'A Dependent') {
      dispatch(setIsFromSelectPatient(true));
      dispatch(setDependentId('1'));
      roleNavigate('/create-your-profile', {
        state: { caseType: selectedCaseType },
      });
    } else {
      dispatch(
        setSelectedPatient({
          value: userInfo.id === selectedPatientId ? userInfo.id : selectedPatientId,
          label: userInfo.id === selectedPatientId ? userInfo.full_name : selectedPatient.patientFullName,
        }),
      );
      dispatch(setSelectedPatientByAdmin({ selectedPatient: selectedPatientProfile }));

      const data = await fetchData();

      if (selectedPatient.id) {
        if (data.length !== 0 && data.includes('patient_profile')) {
          dispatch(setIsFromSelectPatient(true));
          dispatch(
            setDependentId(
              userInfo.id === selectedPatientId
                ? null
                : selectedPatientId.toString(),
            ),
          );
          return roleNavigate('/my-profile', {
            state: {
              caseType: selectedCaseType,
            },
          });
        }
      }

      if (selectedCaseType === 'video_call_scheduled') {
        roleNavigate('/select-provider', {
          state: {
            caseType: 'video_call_scheduled',
            insuranceCarrierId: '',
            customInsuranceCarrier: '',
            userOrganization: currentOrganization.id,
            patientId: selectedPatientId,
          },
        });
      } else {
        roleNavigate('/clinical-data', {
          state: {
            caseType: selectedCaseType,
            patientState: selectedPatientState,
            userOrganization: currentOrganization.id,
            patientId: selectedPatientId,
          },
        });
      }
    }
  };

  useEffect(() => {
    dispatch(setIsFromSelectPatient(false));
    dispatch(setDependentId(null));
  }, [dispatch]);

  return (
    <Container
      isVisibleOnMobile
      containerStyle={{
        justifyContent: screenSize.width >= 564 ? 'center' : 'start',
        overflow: 'hidden',
      }}
      childrenStyle={{ zIndex: '2', paddingTop: screenSize.width > 768 ? '40px' : '16px' }}
      isSidenavVisible={false}
      isIntakeProcess
    >
      <div className={`fs-unmask ${styles.patientFormContainer}`}>
        <PatientIntakeNavigation progress={4} />
        <div className={`${styles.formTitle} fs-unmask mt-3`}>How do you want to review your SmileScan results?</div>
        <form className={styles.formContainer}>
          <div
            className={styles.optionCard}
            style={{ backgroundColor: selectedCaseType === CaseType.video_call_scheduled ? '#E6F4FF' : '' }}
          >
            <div className={styles.optionTitle}>Talk to a dentist on a live video call</div>
            <img src={videoCallImage} alt="Live Video Call" className={styles.optionImage} />
            <MuiButton
              className={styles.optionButton}
              type="button"
              onClick={() => setSelectedCaseType(CaseType.video_call_scheduled)}
              disabled={!videoCaseEnabled}
            >
              Select
            </MuiButton>
          </div>

          <div
            className={styles.optionCard}
            style={{ backgroundColor: selectedCaseType === CaseType.one_report ? '#E6F4FF' : '' }}
          >
            <div className={styles.optionTitle}>Send me a message explaining my results</div>
            <img src={explainResultsImage} alt="Explain Results" className={styles.optionImage} />
            <MuiButton
              className={styles.optionButton}
              type="button"
              onClick={() => setSelectedCaseType(CaseType.one_report)}
              disabled={!asyncCaseEnabled}
            >
              Select
            </MuiButton>
          </div>
        </form>
        <div className={styles.patientFormFooter}>
          <Button variant="text" className={styles.patientFormFooterBack} onClick={() => roleNavigate(-1)}>
            <ArrowBackIosNewIcon />
            Back
          </Button>
          <Button
            variant="contained"
            className={styles.patientFormFooterNext}
            type="button"
            disabled={!selectedCaseType}
            onClick={onSubmit}
          >
            Next
          </Button>
        </div>
      </div>
    </Container>
  );
};
export default SelectCaseType;
